import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable, Optional } from '@frontend/sports/common/core/utils/extended-types';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { DsButtonKind } from '@frontend/ui/button';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { NumpadAction, NumpadSource, StakeChangedEvent } from 'packages/sports/web/app/src/numpad/model';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { NumpadService } from 'packages/sports/web/app/src/numpad/numpad.service';
import { StakeComponent } from 'packages/sports/web/app/src/numpad/stake.component';
import { Observable, combineLatest, map, switchMap, tap } from 'rxjs';

import { betslipSelector } from '../../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../../base/store/state';
import { BetslipGeneratorContextType } from '../../../bet-generation/models';
import { QuickDepositSource } from '../../../quick-deposit/quick-deposit.models';
import { IQuickDepositState } from '../../../quick-deposit/quick-deposit.state';
import { QuickDepositService } from '../../../quick-deposit/services/quick-deposit.service';
import { IBetslipSummary } from '../../../summary/models';
import { BetslipSummaryService } from '../../../summary/services/betslip-summary.service';
import { ITaxationInfoViewModel } from '../../../taxation/components/digital/taxation-info.component';
import { StakeValidationResult } from '../../../validation/models/stake-validation.models';
import { selectCurrentVisibleBetslipErrorsByPriority } from '../../../validation/selectors';
import { EditBetActions } from '../../actions';
import { EditBetErrorService } from '../../services/edit-bet-error.service';
import { IEditMyBetState } from '../../state';

@Component({
    selector: 'bs-betslip-edit-mybet-summary',
    templateUrl: './edit-bet-summary.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [NumpadService],
    styleUrls: ['./edit-bet-summary.scss'],
    standalone: false,
})
export class EditMybetSummaryComponent implements OnInit {
    private betslipState: IBetslipState;
    private EMPTY_COLUMNS_VALUE = '-';
    private STAKE_DIGITS = 2;

    @Input() inColumn = false;
    readonly quickDepositSource = QuickDepositSource.EditMyBet;

    isQuickDepositOpen = false;
    keepDepositOpen = false;
    hasStakeError$: Observable<boolean>;
    stakeError$: Observable<Optional<StakeValidationResult>>;
    summary: IBetslipSummary | undefined;
    isInfoVisible: boolean;
    readonly numpadSource = NumpadSource.EditMyBet;
    @ViewChild(StakeComponent, { static: true }) stakeComponent: StakeComponent;
    readonly buttonThemeKind: ThemeOptions<DsButtonKind> = { 'sportingbet-br': 'tertiary', 'betboo-br': 'tertiary' };
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private summaryService: BetslipSummaryService,
        public siteCore: Sitecore,
        private user: UserService,
        private userSettings: SyncUserSettingsService,
        private interpolationPipe: StringInterpolationPipe,
        public numpadDisplayService: NumpadDisplayService,
        private editMyBetErrorService: EditBetErrorService,
        private changeDetector: ChangeDetectorRef,
        private quickDepositService: QuickDepositService,
        private store: Store<IBetslipRootState>,
    ) {}

    get stake(): Nullable<string> {
        return this.state.addedStake.toFixed(this.STAKE_DIGITS);
    }

    get state(): IEditMyBetState {
        return this.betslipState.types.editBet;
    }

    get isBelowMinStake(): boolean {
        if (this.state.earlyPayoutStake === null) {
            return false;
        }

        const totalStake = new Decimal(this.state.earlyPayoutStake).add(this.state.addedStake);

        return totalStake.lessThan(this.userSettings.configSettings.minimumStake);
    }

    get possibleWinningsTitle(): string {
        return this.user.isAuthenticated &&
            (this.userSettings.configSettings.winningsTaxRate > 0 ||
                this.summary?.taxation?.isPlaceholderTax ||
                this.summary?.taxation?.amount?.greaterThan(0))
            ? this.siteCore.betslip.messages.PossibleWinningsNet
            : this.siteCore.betslip.messages.PossibleWinnings;
    }

    get possibleWinningsNet(): Nullable<Decimal> {
        return this.summary?.possibleWinningsNet ?? null;
    }

    get earlyPayoutStake(): Nullable<Decimal> {
        const earlyPayoutStake = this.state.earlyPayoutStake;

        return earlyPayoutStake === null || new Decimal(earlyPayoutStake).isZero() ? null : new Decimal(earlyPayoutStake);
    }

    get isDisabled(): boolean {
        return this.earlyPayoutStake === null;
    }

    get applyColumnTaxation(): boolean {
        return this.user.columnSize > 0;
    }

    get showAddStakeButton(): boolean {
        return new Decimal(this.state.addedStake).isZero() && !this.numpadDisplayService.isOpen();
    }

    ngOnInit(): void {
        const summary$ = this.summaryService.getSummary$(BetslipGeneratorContextType.EditBetSwap);
        const state$ = this.store.select(betslipSelector).pipe(
            tap((state) => {
                this.betslipState = state;
            }),
        );

        combineLatest([summary$, state$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([summary, state]) => {
                this.summary = summary[1];
                this.handleQuickDepositState(state.quickDeposit);
                this.changeDetector.markForCheck();
            });

        this.stakeError$ = this.store
            .select(selectCurrentVisibleBetslipErrorsByPriority)
            .pipe(switchMap((errors) => this.editMyBetErrorService.getKeypadError(errors)));

        this.hasStakeError$ = this.stakeError$.pipe(map((value) => !!value));

        if (this.state.addedStake !== this.state.confirmedStake) {
            this.dispatchNewStakeValue(this.state.confirmedStake ? this.state.confirmedStake.toFixed() : '0');
        }
    }

    setStake(e: StakeChangedEvent): void {
        this.dispatchNewStakeValue(e.stake, e.action);
    }

    private handleQuickDepositState(state: IQuickDepositState): void {
        const former = this.isQuickDepositOpen;
        this.isQuickDepositOpen = state.isOpen;
        this.keepDepositOpen = state.isProcessing;

        if (!former && state.isOpen && this.numpadDisplayService.isOpen()) {
            this.confirmStake();
            this.numpadDisplayService.close();
        }
    }

    interpolateTaxColumnSize = () => {
        const totalStake = this.summary?.totalStake ?? null;
        const size = this.user.columnSize;
        const columnsValue = this.getColumnsValue(size, totalStake);
        const result = size % 1 === 0 ? size.toFixed(0) : size.toFixed(2);
        const additionalInfo = this.interpolationPipe.transform(this.siteCore.betslip.numpad.ColumnInfo, { taxColumnSize: result });

        return `${this.siteCore.betslip.numpad.Columns} ${columnsValue}; ${additionalInfo}`;
    };

    addStake(): void {
        if (!this.isDisabled && !this.numpadDisplayService.isOpen()) {
            if (this.isQuickDepositOpen) {
                this.quickDepositService.close(true);
            }

            this.stakeComponent.openKeypad();
            this.dispatchStartAddingStake();
        }
    }

    confirmStake(): void {
        this.confirmAddingStake(this.state.addedStake.toString());
    }

    cancelStake(): void {
        this.numpadDisplayService.close();
        this.cancelAddingStake();
    }

    openTaxationPopup(): void {
        if (this.summary?.taxation) {
            this.isInfoVisible = true;
        }
    }

    buildTaxationPopupViewModel(): ITaxationInfoViewModel {
        const taxationLiabilityTitle = this.siteCore.betslip.messages.TaxLiability;

        return {
            possibleWinningsGross: this.summary?.possibleWinnings ?? null,
            possibleWinningsNet: this.possibleWinningsNet,
            taxationRate: this.summary?.taxation?.rate || null,
            taxationAmount: this.summary?.taxation?.amount || null,
            taxationLiabilityTitle,
            taxationLiability: this.summary?.taxation?.liabilityAmount,
        };
    }

    private cancelAddingStake(): void {
        this.store.dispatch(EditBetActions.cancelAddingStake());
    }

    private confirmAddingStake(value: Nullable<string>): void {
        this.store.dispatch(EditBetActions.confirmAddingStake({ stake: new Decimal(value || 0).toNumber() }));
    }

    private dispatchNewStakeValue(stake: Nullable<string>, action?: NumpadAction): void {
        this.store.dispatch(
            EditBetActions.setEditMyBetAddedStake({
                payload: {
                    stake: new Decimal(stake || 0).toNumber(),
                    action,
                },
            }),
        );
    }

    private dispatchStartAddingStake(): void {
        this.store.dispatch(EditBetActions.startAddingStake());
    }

    private getColumnsValue(columnSize: number, stake: Decimal | number | null): string {
        const size = columnSize * 100;

        if (!!stake && size > 0) {
            const decimalStake = new Decimal(stake);
            const totalStakeInSubUnits = decimalStake.times(100);
            const hasRest = totalStakeInSubUnits.modulo(size);
            if (hasRest.equals(0)) {
                return totalStakeInSubUnits.div(size).toFixed(0);
            }
        }

        return this.EMPTY_COLUMNS_VALUE;
    }
}
